import React, { PropsWithChildren, useState, useEffect, useId } from 'react';
import { localImages } from '@root/assets';
import cn from 'classnames';
import styles from './Popup.module.scss';

export interface PopupProps {
  title: string;
  open?: boolean;
  height?: number | string;
  maxHeight?: number | string;
  onClose?: () => void;
  // Uppercase the title via CSS (keeps the i18n key proper-case).
  titleUppercase?: boolean;
  titleClassName?: string;
}

const Popup: React.FC<PropsWithChildren<PopupProps>> = ({
  title,
  onClose,
  children,
  open = false,
  height = 'auto',
  maxHeight,
  titleUppercase,
  titleClassName,
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const [shouldRender, setShouldRender] = useState(open);
  const [availableHeight, setAvailableHeight] = useState<number>(
    window.visualViewport?.height || window.innerHeight
  );
  const titleId = useId();

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      // Use requestAnimationFrame to ensure animation starts correctly
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsOpen(true);
        });
      });

      return () => cancelAnimationFrame(rafId);
    } else {
      setIsOpen(false);
      // Allow time for closing animation
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // 300ms matches the CSS animation duration

      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        setAvailableHeight(window.visualViewport.height);
      } else {
        setAvailableHeight(window.innerHeight);
      }
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const overlayClass = cn(styles.overlay, {
    [styles.overlay_open]: isOpen,
    [styles.overlay_closed]: !isOpen,
  });

  const containerClass = cn(styles.container, {
    [styles.container_open]: isOpen,
    [styles.container_closed]: !isOpen,
  });

  const containerStyle: React.CSSProperties = {
    height: typeof height === 'number' ? `${height}px` : height,
  };

  if (maxHeight) {
    if (typeof maxHeight === 'number') {
      containerStyle.maxHeight = `${maxHeight}px`;
    } else if (maxHeight.includes('vh')) {
      // Account for safe area + visualViewport via a computed CSS calc.
      const vhValue = parseFloat(maxHeight);
      const calculatedVh = `${(availableHeight * vhValue) / 100}px`;
      containerStyle.maxHeight = `min(${calculatedVh}, calc(${maxHeight} - env(safe-area-inset-bottom, 0px)))`;
    } else {
      containerStyle.maxHeight = maxHeight;
    }
  }

  if (!shouldRender) {
    return null;
  }

  return (
    <div className={overlayClass} onClick={handleOverlayClick}>
      <div
        className={containerClass}
        style={containerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={styles.header}>
          <div
            className={cn(
              styles.header__title,
              {
                [styles.header__title_uppercase]: titleUppercase,
              },
              titleClassName
            )}
            id={titleId}
          >
            {title}
          </div>
          <button
            type="button"
            className={styles.header__close}
            onClick={handleClose}
            aria-label="Close"
          >
            <img src={localImages['Icon_Close']} alt="" />
          </button>
        </div>
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};

export default Popup;
